<script setup lang="ts">
import VueApexCharts from 'vue3-apexcharts'
import {useTheme} from 'vuetify'
import {hexToRgb} from '@layouts/utils'
import {computed, ref, watchEffect} from 'vue'
import DashboardStatsService from '@/services/dashboardStatsService'
import {useRootStore} from '@/stores/root'

const vuetifyTheme = useTheme()

interface StatsData {
  live?: number
  staging?: number
  total?: number
}

const rootStore = useRootStore()
const siteStats = ref<StatsData | undefined>(undefined)
const loadingStats = ref(false)
const chartOptions = computed(() => {
  const currentTheme = vuetifyTheme.current.value.colors
  const variableTheme = vuetifyTheme.current.value.variables
  const labelSuccessColor = `rgba(${hexToRgb(currentTheme.success)},0.2)`
  const headingColor = `rgba(${hexToRgb(currentTheme['on-background'])},${
    variableTheme['high-emphasis-opacity']
  })`
  const chartColors = {
    donut: {
      series1: currentTheme.success,
      series2: '#28c76fb3',
      series3: '#28c76f80',
      series4: labelSuccessColor,
    },
  }
  return {
    chart: {
      parentHeightOffset: 0,
      type: 'donut',
    },
    labels: ['Live', 'Staging'],
    colors: [
      chartColors.donut.series1,
      chartColors.donut.series2,
      chartColors.donut.series3,
      chartColors.donut.series4,
    ],
    stroke: { width: 0 },
    dataLabels: {
      enabled: false,
      formatter(val: any) {
        return `${parseInt(val)}%`
      },
    },
    legend: { show: false },
    tooltip: { theme: false },
    grid: {
      padding: {
        top: 15,
        right: -20,
        left: -20,
      },
    },
    states: { hover: { filter: { type: 'none' } } },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            value: {
              fontSize: '1.375rem',
              fontFamily: 'Public Sans',
              color: headingColor,
              fontWeight: 600,
              offsetY: -15,
              formatter(val: any) {
                return `${parseInt(val)}%`
              },
            },
            name: {
              offsetY: 20,
              fontFamily: 'Public Sans',
            },
            total: {
              show: true,
              showAlways: true,
              color: currentTheme.success,
              fontSize: '.8125rem',
              label: 'Total',
              fontFamily: 'Public Sans',
              formatter() {
                return siteStats.value?.total
              },
            },
          },
        },
      },
    },
  }
})

const fetchSiteStats = async () => {
  loadingStats.value = true
  try {
    const statData: StatsData = await DashboardStatsService.fetchSitesStats()
    siteStats.value = statData
  } finally {
    loadingStats.value = false
  }
}
watchEffect(() => {
  fetchSiteStats()
})
</script>

<template>
  <VCard
    :title="
      siteStats && Object.values(siteStats).length && loadingStats
        ? ''
        : 'Website\'s Info'
    "
    width="auto"
  >
    <VDivider />
    <VCardText
      v-if="
        siteStats &&
          Object.values(siteStats).length &&
          Object.values(siteStats).some((stat) => stat !== 0)
      "
      class="d-flex justify-space-between"
    >
      <div class="d-flex flex-column">
        <RouterLink to="sites">
          <VListItemTitle class="font-weight-medium">
            Live
          </VListItemTitle>
          <VListItemSubtitle>
            {{ siteStats?.live }}
          </VListItemSubtitle>
          <VListItemTitle class="font-weight-medium mt-2">
            Staging
          </VListItemTitle>
          <VListItemSubtitle>
            {{ siteStats?.staging }}
          </VListItemSubtitle>
          <VListItemTitle class="font-weight-bold mt-2">
            Total
          </VListItemTitle>
          <VListItemSubtitle>
            {{ siteStats?.total }}
          </VListItemSubtitle>
        </RouterLink>
      </div>
      <div>
        <VueApexCharts
          :options="chartOptions"
          :series="[siteStats?.live, siteStats?.staging]"
          :height="147"
          :width="130"
        />
      </div>
    </VCardText>
    <VCardText
      v-else-if="
        siteStats &&
          Object.values(siteStats).length &&
          Object.values(siteStats).every((stat) => stat === 0)
      "
    >
      You have no sites yet.
      <RouterLink
        class="link"
        to="/sites/add"
      >
        Create one
        <VIcon class="arrow-icon">
          mdi-arrow-right
        </VIcon>
      </RouterLink>
    </VCardText>
    <VCardText
      v-else-if="loadingStats"
      class="w-auto d-flex flex-column align-center justify-center"
    >
      <VProgressCircular
        indeterminate
        color="primary"
        size="70"
      />
      <span class="mt-2"> Loading Stats </span>
    </VCardText>
    <VCardText
      v-else
      class="w-auto d-flex flex-column align-center justify-center"
    >
      <span class="mt-2"> Stats not available </span>
    </VCardText>
  </VCard>
</template>
<style lang="scss" scoped>
.link {
  transition: all 0.3s ease-in-out;

  .arrow-icon {
    margin: 0;
    transition: all 0.3s ease-in-out;
    padding: 0;
    margin-left: -3px;
  }

  &:hover {
    .arrow-icon {
      transform: translateX(4px);
    }
  }
}
</style>
